import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Empty } from "antd";
import ReactCountryFlag from "react-country-flag";

import {
  Charts,
  Loader,
  boxContainer,
  Buttons,
  upload,
  Modal,
  Spacer,
} from "../../components";

import {
  OthersAction,
  UniversityAction,
  SearchAction,
} from "../../store/actions";

import { Images, Colors } from "../../config";

import "./styles.css";

const colors = [
  Colors.main,
  "#5AEE9C",
  "#BE4642",
  "#73C7F6",
  "#35867D",
  "#40DAC2",
];

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profileData = useSelector((state) => state?.profile?.user);
  const {
    dashboardInfo,
    isDashInfoLoading,
    schoolRegPerMonth,
    studentRegPerMonth,
    isRegLoading,
  } = useSelector((state) => state.admin);
  const { popularSubjects, isPopularSubsLoading, onlineUsers } = useSelector(
    (state) => state?.others
  );
  const {
    popularCountries,
    isPopularCountryLoading,
    trending,
    universitiesByBudget,
    isGridLoading,
    isUniByBudgetLoading,
    isAddLoading,
  } = useSelector((state) => state?.university);

  const [currencyValue, setCurrencyValue] = useState("USD");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [popularSubsArray, setPopularSubsArray] = useState([]);

  useEffect(() => {
    if (popularSubjects?.length === 0) {
      dispatch(OthersAction.getPopularSubs());
    }
    if (trending && trending.length === 0) {
      dispatch(UniversityAction.getUniversitiesGrid({}));
    }
    dispatch(UniversityAction.getPopularCountries());
  }, [profileData]);

  useEffect(() => {
    const popularSubsData = popularSubjects?.map((item, index) => ({
      label: item?.program,
      value: item?.value,
      color: colors[index],
    }));
    setPopularSubsArray(popularSubsData);
  }, [popularSubjects]);

  useEffect(() => {
    dispatch(UniversityAction.getUnisByBudget({ currency: currencyValue }));
  }, [profileData, currencyValue]);

  const handleFilterSelectCountry = (label) => {
    let filter = { region: [label] };

    dispatch(
      SearchAction.uniSearch({
        ...filter,
        pageNo: 1,
      })
    );
    navigate("/universities/list");
  };

  const handleFilterSelectBudget = (label) => {
    let filter = {};
    if (label === "0 - 15k") {
      filter = { ...filter, budget: "0-15000" };
    } else if (label === "15k - 30k") {
      filter = { ...filter, budget: "15000-30000" };
    }
    if (label === "30k - 45k") {
      filter = { ...filter, budget: "30000-45000" };
    }
    if (label === "45k - 60k") {
      filter = { ...filter, budget: "45000-60000" };
    }
    if (label === "> 60k") {
      filter = { ...filter, budget: "60000-" };
    }
    dispatch(
      SearchAction.uniSearch({
        ...filter,
        pageNo: 1,
      })
    );
    navigate("/universities/list");
  };

  const labelMapping = {
    fifteenToThirty: "15k - 30k",
    fourtyFiveToSixty: "45k - 60k",
    moreThanSixty: "> 60k",
    thirtyToFourtyFive: "30k - 45k",
    zeroToFifteen: "0 - 15k",
  };

  const dataset = [
    {
      choice1: 59,
      choice2: 57,
      choice3: 86,
      country: "Australia",
      countryCode: "AU",
      countryId: 5,
    },
    {
      choice1: 47,
      choice2: 53,
      choice3: 106,
      country: "UK",
      countryCode: "GB",
      countryId: 2,
    },
    {
      choice1: 54,
      choice2: 56,
      choice3: 92,
      country: "USA",
      countryCode: "US",
      countryId: 1,
    },
  ];

  const uData = [59, 57, 86];
  const pData = [47, 53, 106];
  const qData = [54, 56, 92];
  const yLabels = ["Australia", "UK", "USA"];

  const xlabels = Object.keys(universitiesByBudget).map(
    (key) => labelMapping[key]
  );
  const xdata = Object.values(universitiesByBudget);

  const studentsCount = [
    {
      title: "Total",
      value: dashboardInfo?.schoolStudentsCount ?? 0,
      icon: Images.HomeTotal,
    },
    {
      title: "Active",
      value: dashboardInfo?.registeredSchoolStudentsCount ?? 0,
      icon: Images.HomeActiveStudents,
    },
  ];
  const individualStudentsCount = [
    {
      title: "Total",
      value: dashboardInfo?.individualStudentsCount ?? 0,
      icon: Images.HomeTotal,
    },
    {
      title: "Active",
      value: dashboardInfo?.activeIndividualStudentsCount ?? 0,
      icon: Images.HomeActiveStudents,
    },
  ];
  const schoolsCount = [
    {
      title: "Total",
      value: dashboardInfo?.schoolsCount ?? 0,
      icon: Images.HomeTotal,
    },
    {
      title: "Active",
      value: dashboardInfo?.activeSchoolsCount ?? 0,
      icon: Images.HomeActiveStudents,
    },
  ];

  const uploadButtons = [
    <upload.UploadUni text="University Sheets" width="full" />,
    <upload.UploadUniImages text="University Images" width="full" />,
    <upload.UploadVisaInfo width="full" text="Visa information" />,
    <upload.UploadJobs width="full" text="Internships" />,
  ];

  return (
    <div className="home-container">
      <div className="flex w-full justify-between items-center">
        <div className="text-[2em] font-bold">
          Hi {profileData?.firstName} {profileData?.lastName}, welcome back
        </div>
        <div className="w-[12em]">
          <Buttons.DefaultButton
            size="medium"
            width="full"
            onClick={() => setUploadOpen(true)}
            disabled={isAddLoading}
          >
            Upload
          </Buttons.DefaultButton>
        </div>
      </div>
      <Spacer height={20} />
      <div className="home-first-row">
        <div className="flex flex-col gap-3 h-full">
          <div className="home-box-title" title="Students">
            Students
          </div>
          <boxContainer.LiteBoxContainer addstyle="h-[21em]">
            <div className="flex flex-col justify-center h-full gap-2">
              {isDashInfoLoading ? (
                <Loader.Circular />
              ) : (
                studentsCount?.map((item, index) => (
                  <div
                    className={`flex gap-4 items-center py-4 px-3 ${
                      index < 1 && "border-b border-gray-200"
                    }`}
                    key={index}
                  >
                    <img
                      src={item.icon}
                      className="h-[3.75em] w-[3.75em]"
                      alt="icon"
                    />
                    <div>
                      <div className="text-[1.25em] font-medium">
                        {item.title}
                      </div>
                      <div className="text-[1.75em] font-bold leading-[1.975em]">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3 h-full">
          <div className="home-box-title" title="Students">
            Individual Students
          </div>
          <boxContainer.LiteBoxContainer addstyle="h-[21em]">
            <div className="flex flex-col justify-center h-full gap-2">
              {isDashInfoLoading ? (
                <Loader.Circular />
              ) : (
                individualStudentsCount?.map((item, index) => (
                  <div
                    className={`flex gap-4 items-center py-4 px-3 ${
                      index < 1 && "border-b border-gray-200"
                    }`}
                    key={index}
                  >
                    <img
                      src={item.icon}
                      className="h-[3.75em] w-[3.75em]"
                      alt="icon"
                    />
                    <div>
                      <div className="text-[1.25em] font-medium">
                        {item.title}
                      </div>
                      <div className="text-[1.75em] font-bold leading-[1.975em]">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3 h-full">
          <div className="home-box-title" title="Students">
            Schools
          </div>
          <boxContainer.LiteBoxContainer addstyle="h-[21em]">
            <div className="flex flex-col justify-center h-full gap-2">
              {isDashInfoLoading ? (
                <Loader.Circular />
              ) : (
                schoolsCount?.map((item, index) => (
                  <div
                    className={`flex gap-4 items-center py-4 px-3 ${
                      index < 1 && "border-b border-gray-200"
                    }`}
                    key={index}
                  >
                    <img
                      src={item.icon}
                      className="h-[3.75em] w-[3.75em]"
                      alt="icon"
                    />
                    <div>
                      <div className="text-[1.25em] font-medium">
                        {item.title}
                      </div>
                      <div className="text-[1.75em] font-bold leading-[1.975em]">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3 h-full">
          <div className="home-box-title" title="Popular Subjects">
            Popular Subjects
          </div>
          <boxContainer.LiteBoxContainer addstyle="h-[21em]">
            <div className="h-full flex items-center justify-center">
              {isPopularSubsLoading ? (
                <Loader.Circular />
              ) : popularSubsArray?.length === 0 ? (
                <Empty />
              ) : (
                <div
                  className="flex justify-around items-center gap-6"
                  style={{
                    backgroundImage: `url(${Images.WorldMapDotted})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                  }}
                >
                  <div>
                    <Charts.PieChartPopularSubs
                      data={popularSubsArray}
                      onItemClick={(event, batItemIdentifier) => {
                        const dataIndex = batItemIdentifier.dataIndex;
                        const label = dataset[dataIndex]?.countryId;
                        handleFilterSelectCountry(label);
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-5 flex-wrap  font-semibold">
                    {popularSubsArray?.map((item, index) => {
                      return (
                        <div
                          className="flex gap-2 items-center justify-left"
                          key={index}
                        >
                          <div
                            className="h-[0.625em] w-[1.875em]"
                            style={{ backgroundColor: colors[index] }}
                          ></div>
                          <div className="text-[0.875em]">{item?.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3">
          <div
            className="home-box-title"
            title="Total Universities by Budget (USD)"
          >
            Total Universities by Budget (USD)
          </div>

          <boxContainer.LiteBoxContainer>
            {isUniByBudgetLoading ? (
              <div className="h-[18.875em] flex items-center justify-center">
                <Loader.Circular />
              </div>
            ) : (
              <>
                {!universitiesByBudget?.zeroToFifteen ? (
                  <div className="h-[21em] flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundImage: `url(${Images.WorldMapDotted})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      height: "18.75em",
                    }}
                  >
                    <Charts.VerticalBarChart
                      xdata={xlabels}
                      series={[{ data: xdata }]}
                      onItemClick={(event, batItemIdentifier) => {
                        const dataIndex = batItemIdentifier.dataIndex;
                        const label =
                          labelMapping[
                            Object.keys(universitiesByBudget)[dataIndex]
                          ];

                        handleFilterSelectBudget(label);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </boxContainer.LiteBoxContainer>
        </div>
      </div>
      <div className="home-second-row">
        <div className="flex flex-col gap-3">
          <div className="home-box-title" title="Registrations per Month">
            Registrations per Month (Students)
          </div>

          <boxContainer.LiteBoxContainer>
            {isRegLoading ? (
              <div className="h-[18.875em] flex items-center justify-center">
                <Loader.Circular />
              </div>
            ) : (
              <>
                {!studentRegPerMonth ? (
                  <div className="h-[18.875em] flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundImage: `url(${Images.WorldMapDotted})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      height: "18.875em",
                    }}
                  >
                    <Charts.AreaChart
                      dataToShow={Charts.getLast12MonthsData(
                        studentRegPerMonth
                      )}
                      series={[
                        {
                          id: "Students",
                          label: "Students",
                          dataKey: "count",
                          stack: "total",
                          area: true,
                          showMark: false,
                          color: "#54aea1",
                        },
                      ]}
                    />
                  </div>
                )}
              </>
            )}
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3">
          <div className="home-box-title" title="Registrations per Month">
            Registrations per Month (Schools)
          </div>

          <boxContainer.LiteBoxContainer>
            {isRegLoading ? (
              <div className="h-[18.875em] flex items-center justify-center">
                <Loader.Circular />
              </div>
            ) : (
              <>
                {!schoolRegPerMonth ? (
                  <div className="h-[18.875em] flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundImage: `url(${Images.WorldMapDotted})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      height: "18.875em",
                    }}
                  >
                    <Charts.AreaChart
                      dataToShow={Charts.getLast12MonthsData(schoolRegPerMonth)}
                      series={[
                        {
                          id: "School",
                          label: "School",
                          dataKey: "count",
                          stack: "total",
                          area: true,
                          showMark: false,
                          color: "#73a59e",
                        },
                      ]}
                    />
                  </div>
                )}
              </>
            )}
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex flex-col gap-3">
          <div className="home-box-title" title="Popular Countries">
            Popular Countries
          </div>
          <boxContainer.LiteBoxContainer>
            {isPopularCountryLoading ? (
              <div className="h-[18.875em] flex items-center justify-center">
                <Loader.Circular />
              </div>
            ) : (
              <>
                {popularCountries?.length === 0 ? (
                  <div className="h-[18.875em] flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundImage: `url(${Images.WorldMapDotted})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      height: "18.875em",
                      position: "relative",
                    }}
                  >
                    <div className="absolute h-[16.25em] w-[11.25em] left-0 top-0 flex flex-col justify-between">
                      {dataset?.map((item, index) => (
                        <div
                          className="flex flex-1 w-full items-center justify-left gap-2 pl-5"
                          key={index}
                        >
                          <ReactCountryFlag
                            countryCode={item.countryCode}
                            svg
                            style={{
                              width: "1.625em",
                              height: "1.625em",
                            }}
                            title={item.countryCode}
                          />
                          <div>{item?.country}</div>
                        </div>
                      ))}
                    </div>
                    <Charts.HorizontalBarChart
                      ydata={yLabels}
                      series={[
                        {
                          label: "1st Choice",
                          data: uData,
                          color: "#387f75",
                        },
                        {
                          label: "2nd Choice",
                          data: pData,
                          color: "#5aee9c",
                        },
                        {
                          label: "3rd Choice",
                          data: qData,
                          color: "#bc4642",
                        },
                      ]}
                    />
                    <div className="absolute flex gap-2 bottom-2 left-2">
                      <div className="popular-countries-legend">
                        <span className="h-3 w-4 bg-[#387f75]"></span>
                        1st Choice
                      </div>
                      <div className="popular-countries-legend">
                        <span className="h-3 w-4 bg-[#5aee9c]"></span>
                        2nd Choice
                      </div>
                      <div className="popular-countries-legend">
                        <span className="h-3 w-4 bg-[#bc4642]"></span>
                        3rd Choice
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </boxContainer.LiteBoxContainer>
        </div>
      </div>
      <div className="home-third-row"></div>
      <Modal.DefaultModal
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
      >
        <div className="w-[35em] flex flex-col gap-4 justify-center items-center">
          <div className="text-main font-bold text-[2em]">Upload Data</div>
          <div className="flex justify-center items-center gap-3 flex-wrap">
            {uploadButtons?.map((item, index) => (
              <div key={index}>{item}</div>
            ))}
          </div>
        </div>
      </Modal.DefaultModal>
    </div>
  );
};

export default Home;
